/**
 * The JSON Schema subset that `page-schemas.generated.json` uses (written by
 * `pnpm content:schemas` in web/ from the site's Zod schemas), plus helpers to
 * build empty values, merge saved content with defaults and validate before saving.
 */
export interface JsonSchema {
  type?: 'object' | 'array' | 'string' | 'number' | 'integer' | 'boolean' | 'null';
  properties?: Record<string, JsonSchema>;
  required?: string[];
  items?: JsonSchema;
  anyOf?: JsonSchema[];
  enum?: string[];
  default?: unknown;
  minLength?: number;
  maxLength?: number;
  pattern?: string;
  minItems?: number;
  maxItems?: number;
  minimum?: number;
  maximum?: number;
  description?: string;
}

export interface PageDefinition {
  name: string;
  schema: JsonSchema;
  defaults: unknown;
  /** Blocks that, saved exactly like this, were never edited: the site shows the current default instead. */
  untouched?: Record<string, unknown>;
}

export interface ValidationError {
  path: string;
  message: string;
}

/** `anyOf: [X, null]` is how a nullable field is exported. */
export function nullableInner(schema: JsonSchema): JsonSchema | null {
  if (!schema.anyOf || schema.anyOf.length !== 2) return null;
  const inner = schema.anyOf.find((option) => option.type !== 'null');
  const hasNull = schema.anyOf.some((option) => option.type === 'null');
  return inner && hasNull ? inner : null;
}

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** A fresh value for a schema: defaults where given, otherwise empty but valid in shape. */
export function emptyValue(schema: JsonSchema): unknown {
  if (schema.default !== undefined) return JSON.parse(JSON.stringify(schema.default));
  if (nullableInner(schema)) return null;
  if (schema.enum && schema.enum.length) return schema.enum[0];
  switch (schema.type) {
    case 'object': {
      const result: Record<string, unknown> = {};
      for (const [key, property] of Object.entries(schema.properties || {})) result[key] = emptyValue(property);
      return result;
    }
    case 'array': {
      const count = schema.minItems || 0;
      return Array.from({ length: count }, () => (schema.items ? emptyValue(schema.items) : ''));
    }
    case 'boolean':
      return false;
    case 'number':
    case 'integer':
      return 0;
    default:
      return '';
  }
}

/**
 * Saved content on top of the defaults. Keys the schema does not know are kept
 * (older pages store `{ type, value }` fields next to the structured ones).
 */
export function mergeWithDefaults(schema: JsonSchema, saved: unknown, defaults: unknown): unknown {
  const inner = nullableInner(schema);
  if (inner) return saved === null || saved === undefined ? (defaults === undefined ? null : defaults) : mergeWithDefaults(inner, saved, defaults === null ? emptyValue(inner) : defaults);
  if (schema.type === 'object' && schema.properties) {
    const base = isRecord(defaults) ? defaults : {};
    const result: Record<string, unknown> = isRecord(saved) ? { ...saved } : {};
    for (const [key, property] of Object.entries(schema.properties)) {
      const fallback = key in base ? base[key] : emptyValue(property);
      result[key] = isRecord(saved) && key in saved ? mergeWithDefaults(property, saved[key], fallback) : JSON.parse(JSON.stringify(fallback));
    }
    return result;
  }
  if (schema.type === 'array') {
    if (!Array.isArray(saved)) return JSON.parse(JSON.stringify(Array.isArray(defaults) ? defaults : []));
    return saved.map((item) => (schema.items ? mergeWithDefaults(schema.items, item, emptyValue(schema.items)) : item));
  }
  return saved === undefined ? defaults : saved;
}

/** Equal JSON, whatever the order of the keys (the database returns them in its own order). */
function sameContent(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (Array.isArray(a) || Array.isArray(b)) return Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((item, index) => sameContent(item, b[index]));
  if (!isRecord(a) || !isRecord(b)) return false;
  const keys = Object.keys(a);
  return keys.length === Object.keys(b).length && keys.every((key) => key in b && sameContent(a[key], b[key]));
}

/**
 * Saved content with the blocks that still hold an old default replaced by the current default,
 * as the site shows them (getHomeContent). That includes a block saved again later with the
 * fields added since at their defaults, so both sides are also compared after filling those in.
 */
export function replaceUntouched(schema: JsonSchema, saved: unknown, definition: PageDefinition): unknown {
  const { untouched, defaults } = definition;
  if (!untouched || !isRecord(saved) || !isRecord(defaults)) return saved;
  const result = { ...saved };
  for (const [key, old] of Object.entries(untouched)) {
    const property = schema.properties && schema.properties[key];
    if (!property || !(key in saved) || !(key in defaults)) continue;
    // Like the site's schema parse: missing fields get the schema's own default, not the page's.
    const filled = (value: unknown): unknown => mergeWithDefaults(property, value, undefined);
    if (sameContent(saved[key], old) || sameContent(filled(saved[key]), filled(old))) result[key] = JSON.parse(JSON.stringify(defaults[key]));
  }
  return result;
}

export function validate(schema: JsonSchema, value: unknown, path = ''): ValidationError[] {
  const errors: ValidationError[] = [];
  const inner = nullableInner(schema);
  if (inner) return value === null ? [] : validate(inner, value, path);
  if (schema.enum) {
    if (typeof value !== 'string' || !schema.enum.includes(value)) errors.push({ path, message: `moet één van ${schema.enum.join(', ')} zijn` });
    return errors;
  }
  switch (schema.type) {
    case 'object':
      if (!isRecord(value)) return [{ path, message: 'is verplicht' }];
      for (const [key, property] of Object.entries(schema.properties || {})) {
        const childPath = path ? `${path}.${key}` : key;
        if (!(key in value)) {
          if ((schema.required || []).includes(key)) errors.push({ path: childPath, message: 'is verplicht' });
          continue;
        }
        errors.push(...validate(property, value[key], childPath));
      }
      break;
    case 'array':
      if (!Array.isArray(value)) return [{ path, message: 'moet een lijst zijn' }];
      if (schema.minItems !== undefined && value.length < schema.minItems) errors.push({ path, message: `minstens ${schema.minItems} item(s) nodig` });
      if (schema.maxItems !== undefined && value.length > schema.maxItems) errors.push({ path, message: `hoogstens ${schema.maxItems} item(s) toegelaten` });
      if (schema.items) value.forEach((item, index) => errors.push(...validate(schema.items as JsonSchema, item, `${path}[${index + 1}]`)));
      break;
    case 'string':
      if (typeof value !== 'string') return [{ path, message: 'moet tekst zijn' }];
      if (schema.minLength !== undefined && value.trim().length < schema.minLength) errors.push({ path, message: 'mag niet leeg zijn' });
      if (schema.maxLength !== undefined && value.length > schema.maxLength) errors.push({ path, message: `hoogstens ${schema.maxLength} tekens` });
      if (schema.pattern && !new RegExp(schema.pattern).test(value)) errors.push({ path, message: value === '' ? 'mag niet leeg zijn' : patternMessage(schema.pattern) });
      break;
    case 'boolean':
      if (typeof value !== 'boolean') errors.push({ path, message: 'moet aan of uit zijn' });
      break;
    case 'number':
    case 'integer':
      if (typeof value !== 'number' || Number.isNaN(value)) return [{ path, message: 'moet een getal zijn' }];
      if (schema.type === 'integer' && !Number.isInteger(value)) errors.push({ path, message: 'moet een geheel getal zijn' });
      if (schema.minimum !== undefined && value < schema.minimum) errors.push({ path, message: `minstens ${schema.minimum}` });
      if (schema.maximum !== undefined && value > schema.maximum) errors.push({ path, message: `hoogstens ${schema.maximum}` });
      break;
  }
  return errors;
}

/** Pattern the site's Zod schemas use for a yyyy-mm-dd date. */
const DATE_PATTERN = '^\\d{4}-\\d{2}-\\d{2}$';

/** Pattern the site's Zod schemas use for the part of an address, e.g. a story slug. */
const SLUG_PATTERN = '^[a-z0-9-]{1,80}$';

/** Pattern for ids that end up in an address, e.g. a game round. */
const ID_PATTERN = '^[a-zA-Z0-9_-]{1,80}$';

function patternMessage(pattern: string): string {
  if (pattern === SLUG_PATTERN) return 'alleen kleine letters, cijfers en streepjes';
  if (pattern === DATE_PATTERN) return 'moet een datum zijn';
  if (pattern === ID_PATTERN) return 'alleen letters, cijfers, - en _';
  return 'heeft niet de juiste vorm';
}

/** "Desinformatie doorheen de geschiedenis!" → "desinformatie-doorheen-de-geschiedenis". */
export function slugify(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80)
    .replace(/-+$/, '');
}

/** Widget for a string field, decided by its key because the schema carries no formats. */
export type StringWidget = 'text' | 'textarea' | 'richtext' | 'html' | 'image' | 'video' | 'audio' | 'file' | 'url' | 'date';

function isDateSchema(schema: JsonSchema): boolean {
  return schema.pattern === DATE_PATTERN || (schema.anyOf || []).some((option) => option.pattern === DATE_PATTERN);
}

/** Help text that marks a rich text field (see textBlocks on the site). */
export const RICH_TEXT_HINT = 'met de knoppen boven de tekst';

const TEXTAREA_KEYS = /^(description|text|intro|summary|explanation|body|answer|lead|question|what|why|tagline|heroTagline|mission|quote|paragraph|note|subtitle)$/i;

export function stringWidget(key: string, schema: JsonSchema, value: unknown): StringWidget {
  if (isDateSchema(schema)) return 'date';
  if (/html$/i.test(key)) return 'html';
  if (/(image|photo|thumbnail|poster)(Url)?$/i.test(key) || key === 'src') return 'image';
  if (key === 'videoFile') return 'video';
  if (key === 'audioFile') return 'audio';
  if (key === 'fileUrl') return 'file';
  if (/^(href|url|link|videoUrl|fileUrl)$/i.test(key) || /Url$/.test(key)) return 'url';
  // Formatted text the site shows with bold, italic, links and bullet lists: edited with buttons (RichTextField).
  if (schema.description?.includes(RICH_TEXT_HINT)) return 'richtext';
  if (TEXTAREA_KEYS.test(key)) return 'textarea';
  // A field whose help says Enter starts a new line needs a box that takes Enter.
  if (schema.description?.includes('Enter = nieuwe regel')) return 'textarea';
  if (typeof value === 'string' && value.length > 80) return 'textarea';
  if (typeof schema.default === 'string' && schema.default.length > 80) return 'textarea';
  return 'text';
}

/**
 * A list whose help says it is sorted alphabetically (the glossary): the CMS shows its items by name
 * and leaves out moving them, since the site sorts them anyway.
 */
export function isAlphabetical(schema: JsonSchema): boolean {
  return [schema, nullableInner(schema)].some((part) => Boolean(part?.description?.includes('Alfabetisch gesorteerd')));
}

/** Dutch alphabetical order of item names, ignoring case and accents. */
export function byName(a: string, b: string): number {
  return a.localeCompare(b, 'nl', { sensitivity: 'base' });
}

/** A link field: a page of the site or any address. */
export function isLinkKey(key: string): boolean {
  return /^(href|url|link)$/i.test(key);
}

/** The factcheck categories of a bundle are category ids, picked from the list. */
export function isCategoryList(key: string, parentKey: string, schema: JsonSchema): boolean {
  return key === 'categories' && parentKey === 'factchecks' && schema.type === 'array' && schema.items?.type === 'string';
}

/**
 * Fields that only apply to one layout of a series (The bigger picture): a series of
 * decks has slides, a series of bundles has factchecks. Hidden under the other layout.
 */
const LAYOUT_ONLY: Record<string, string> = { slides: 'slides', factchecks: 'factchecks' };

export function hiddenByLayout(key: string, layout: string): boolean {
  return Boolean(layout) && key in LAYOUT_ONLY && LAYOUT_ONLY[key] !== layout;
}

/** What a homepage card that follows a series takes from that series' newest story (see followBiggerPicture on the site). */
const FOLLOWED = ['title', 'subtitle', 'tag', 'caption', 'imageUrl', 'href'];

/** Whether a group follows a series (the homepage's bigger picture cards with "Volg reeks" on). */
export function followsSeries(schema: JsonSchema, value: unknown): boolean {
  return Boolean(schema.properties && schema.properties.follow) && isRecord(value) && value.follow === true;
}

/** A field kept for content saved before its replacement ("Verouderd: …"): shown only while it still holds something. */
export function hiddenAsOutdated(schema: JsonSchema, value: unknown): boolean {
  if (!schema.description?.startsWith('Verouderd')) return false;
  return value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0);
}

/** The fields a card that follows a series does not use: they come from the story, so they are hidden. */
export function hiddenByFollow(key: string, schema: JsonSchema, value: unknown): boolean {
  return followsSeries(schema, value) && FOLLOWED.includes(key);
}

/**
 * Mens of machine: an option is a photo, a video, a sound fragment or a text, and its `kind` says which. Only the
 * fields of that kind are shown, instead of every upload at once.
 */
const MEDIA_FIELDS: Record<string, string[]> = {
  photo: ['imageUrl', 'revealImageUrl'],
  video: ['videoFile', 'imageUrl'],
  audio: ['audioFile', 'imageUrl', 'text'],
  text: ['text'],
};
const MEDIA_KEYS = ['imageUrl', 'revealImageUrl', 'videoFile', 'audioFile', 'text'];

/** The kind of a game option, or null for any other group. An option saved before there was a kind gets it from what is filled in. */
export function mediaKind(schema: JsonSchema, value: unknown): string | null {
  if (!schema.properties?.kind || !schema.properties.videoFile) return null;
  const option = isRecord(value) ? value : {};
  if (typeof option.kind === 'string' && option.kind in MEDIA_FIELDS) return option.kind;
  if (option.videoFile) return 'video';
  if (option.audioFile) return 'audio';
  return option.text && !option.imageUrl ? 'text' : 'photo';
}

/** The fields of a game option that its kind does not use. */
export function hiddenByMediaKind(key: string, schema: JsonSchema, value: unknown): boolean {
  const kind = mediaKind(schema, value);
  return kind !== null && MEDIA_KEYS.includes(key) && !MEDIA_FIELDS[kind].includes(key);
}

/** Another kind for a game option: the fields the new kind does not use are emptied, so the site shows what was picked. */
export function withMediaKind(value: Record<string, unknown>, kind: string): Record<string, unknown> {
  const next: Record<string, unknown> = { ...value, kind };
  for (const key of MEDIA_KEYS) if (!(MEDIA_FIELDS[kind] || []).includes(key)) next[key] = '';
  return next;
}

const ENUM_LABELS: Record<string, string> = {
  photo: 'Foto', video: 'Video', audio: 'Geluidsfragment', text: 'Tekst (bv. een mop)',
  slides: 'Verhalen met slides', factchecks: 'Gebundelde factchecks',
  pdf: 'PDF', doc: 'Document', ebook: 'E-book',
  laag: 'Laag', matig: 'Matig', hoog: 'Hoog', 'zeer-hoog': 'Zeer hoog',
  foto: 'Foto (beeldmanipulatie)', tandwiel: 'Tandwiel', schaar: 'Schaar',
  maskers: 'Maskers', spion: 'Spion', 'niet-gelijk': 'Niet gelijk (≠)',
  spreekballon: 'Spreekballon met kruis', lach: 'Lachend gezicht', vishaak: 'Vishaak', sterren: 'Sterretjes (AI)',
  wegwijzer: 'Wegwijzer', megafoon: 'Megafoon', grafiek: 'Grafiek', robot: 'Robot',
  beker: 'Beker', filmrol: 'Filmrol (bioscoop)', klapbord: 'Klapbord (film)', ticket: 'Ticket (concert, theater)', cadeau: 'Cadeau', boek: 'Boek',
  game: 'Gamecontroller', koptelefoon: 'Koptelefoon (muziek, podcast)',
};

/** The picture of a choice, a file of the site: the graphics of a technique (web/public/techniques) and the icons of the contest (web/public/contest). */
const ENUM_PREVIEWS: Record<string, string> = {
  foto: '/home/actua/v3-technique.webp',
  ...Object.fromEntries(
    ['tandwiel', 'schaar', 'maskers', 'spion', 'niet-gelijk', 'spreekballon', 'lach', 'vishaak', 'sterren', 'wegwijzer', 'megafoon', 'grafiek', 'robot']
      .map((icon) => [icon, `/techniques/${icon}.svg`]),
  ),
  ...Object.fromEntries(['beker', 'filmrol', 'klapbord', 'ticket', 'cadeau', 'boek', 'game', 'koptelefoon'].map((icon) => [icon, `/contest/${icon}.svg`])),
};

export interface EnumItem {
  text: string;
  value: string;
  /** Site path of its picture, if it has one. */
  preview?: string;
}

export function enumItems(values: string[]): EnumItem[] {
  return values.map((value) => ({ text: ENUM_LABELS[value] || value, value, preview: ENUM_PREVIEWS[value] }));
}

/** True when the choices come with pictures, so the picker shows them. */
export const hasEnumPreviews = (values: string[]): boolean => values.some((value) => value in ENUM_PREVIEWS);

/** A technique of the factchecks of the week, as the site validates it. */
export interface TechniqueOption {
  name: string;
  what: string;
  why: string;
  detectionSteps: string[];
}

/** "Schijnverband creëren" and "schijnverband creeren " are the same technique. */
export function techniqueKey(name: string): string {
  return name.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/\s+/g, ' ').trim();
}

/** The name of a used technique picks from the techniques used before, so the archive of techniques gets no spelling variants. */
export function isTechniqueName(key: string, parentKey: string): boolean {
  return key === 'name' && parentKey === 'technique';
}

function asTechnique(value: unknown): TechniqueOption | null {
  if (!isRecord(value) || typeof value.name !== 'string' || !value.name.trim()) return null;
  const steps = Array.isArray(value.detectionSteps) ? value.detectionSteps.filter((step): step is string => typeof step === 'string') : [];
  // A week still being filled in (only a name) is no example to copy from.
  if (!value.what && !value.why && steps.every((step) => !step.trim())) return null;
  return { name: value.name.trim(), what: typeof value.what === 'string' ? value.what : '', why: typeof value.why === 'string' ? value.why : '', detectionSteps: steps };
}

/**
 * Every technique in the page (every `technique` object, at any depth), once per name, in page order: the
 * factchecks of the week are newest first, so a technique keeps the texts of its latest week.
 */
export function techniqueOptions(page: unknown): TechniqueOption[] {
  const found = new Map<string, TechniqueOption>();
  const walk = (value: unknown): void => {
    if (Array.isArray(value)) {
      value.forEach(walk);
      return;
    }
    if (!isRecord(value)) return;
    for (const [key, child] of Object.entries(value)) {
      const technique = key === 'technique' ? asTechnique(child) : null;
      if (technique && !found.has(techniqueKey(technique.name))) found.set(techniqueKey(technique.name), technique);
      walk(child);
    }
  };
  walk(page);
  return [...found.values()];
}

/** Fields that hold factcheck ids get a search: one id, or a list of them. */
export type FactcheckPicker = 'one' | 'many';

export function factcheckPicker(key: string, parentKey: string, schema: JsonSchema): FactcheckPicker | null {
  if (key === 'factcheckId' && schema.type === 'string') return 'one';
  const listKey = key === 'factcheckIds' || (key === 'ids' && parentKey === 'factchecks');
  if (listKey && schema.type === 'array' && schema.items?.type === 'string') return 'many';
  return null;
}

const LABELS: Record<string, string> = {
  parts: 'Alinea’s', video: 'Video',
  explainer: 'Uitleg (enkel op de pagina van de game)',
  title: 'Titel', intro: 'Intro', description: 'Omschrijving', text: 'Tekst', items: 'Items', source: 'Bron', author: 'Auteur', authorDescription: 'Omschrijving auteur',
  quote: 'Quote', word: 'Woord', stats: 'Cijfers', number: 'Getal', label: 'Label', name: 'Naam', role: 'Functie', imageUrl: 'Afbeelding',
  alt: 'Alt-tekst (toegankelijkheid)', href: 'Link', url: 'Link', ctaLabel: 'Knoptekst', cta: 'Knop', visible: 'Zichtbaar', question: 'Vraag',
  options: 'Opties', explanation: 'Uitleg', isReal: 'Echt (niet met AI gemaakt)', isClickbait: 'Dit is de clickbait titel', articleUrl: 'Oorspronkelijk artikel (link)', games: 'Games',
  headlines: 'Headline of Headlie', whatsapp: 'WhatsApp quiz', hero: 'Hero', search: 'Zoeken', promo: 'Promobanner', factcheckOfWeek: 'Factcheck van de week',
  technique: 'Gebruikte techniek', wordOfWeek: 'Woord van de week', quoteOfWeek: 'Quote van de week', infographic: 'Infographic van de maand',
  tools: 'Zeker ook eens checken', biggerPicture: 'The bigger picture', behindTheScenes: 'Achter de schermen', blog: 'Blog',
  team: 'Team', values: 'Kernwaarden', trust: 'Waarom ons vertrouwen', together: 'Samen', mission: 'Wat doen we', heroTitle: 'Hero titel',
  heroTagline: 'Hero tagline', sections: 'Secties', bullets: 'Opsomming', lead: 'Vetgedrukt begin', steps: 'Stappen', tips: 'Tips', example: 'Voorbeeld',
  paragraphs: "Alinea's", terms: 'Termen', term: 'Term', definition: 'Definitie', videos: "Video's", videoUrl: 'Video-URL', fileType: 'Bestandstype',
  fileUrl: 'Bestand', downloads: 'Downloads', partners: 'Partners', supporters: 'Steunende partners', factcheckers: 'Factcheckpartners', width: 'Breedte (px)', height: 'Hoogte (px)', crop: 'Uitsnede foto', tagline: 'Tagline', subtitle: 'Ondertitel', chips: 'Chips',
  suggestions: 'Zoeksuggesties', factcheckId: 'Factcheck', factcheckIds: 'Factchecks', factchecks: 'Factchecks', blogIds: "Blog-id's", detectionSteps: 'Hoe detecteer je het', what: 'Wat is het',
  why: 'Waarom werkt het', tag: 'Tag', body: 'Tekst', date: 'Datum',
  flash: 'Flashbericht bovenaan', placeholder: 'Tekst in het lege zoekveld', examples: 'Voorbeelden', intents: 'Snelkoppelingen',
  caption: 'Bijschrift', linkLabel: 'Linktekst', overview: 'Link naar het overzicht', partner: 'Partner', posterUrl: 'Afbeelding',
  members: 'Teamleden', left: 'Linkerkolom', right: 'Rechterkolom', closing: 'Slotzin', support: 'Steun ons', help: 'Help ons',
  supportersLabel: 'Titel steunende partners', factcheckersLabel: 'Titel factcheckpartners', heroText: 'Hero tekst',
  examplesTitle: 'Titel voorbeelden', difference: 'Het verschil dat we maken', impact: 'Impact', donate: 'Doneren',
  direct: 'Zonder fiscaal attest', conditions: 'Voorwaarden', transfer: 'Overschrijving (leeg = verborgen)', attest: 'Met fiscaal attest',
  note: 'Voetnoot (voor de link)', noteLink: 'Voetnoot link', noteEnd: 'Voetnoot (na de link)', benefits: 'Voordelen', form: 'Formulier',
  photoGame: 'Mens of machine', headlineGame: 'Headline of Headlie', instruction: 'Opdracht', rounds: 'Rondes', id: 'ID (in de link)',
  revealImageUrl: 'Afbeelding na antwoord (met omcirkelde details)', credit: 'Maker en bron', listTitle: 'Titel boven de lijst',
  meta: 'Korte omschrijving', imageAlt: 'Alt-tekst afbeelding (toegankelijkheid)', series: 'Reeksen', layout: 'Soort reeks',
  moreTitle: 'Titel boven de andere verhalen', slug: 'Adres (in de link)', card: 'Kaart', slides: 'Slides', query: 'Zoekterm',
  topic: 'Onderwerp', theme: 'Thema', risk: 'Risico', prize: 'Prijs', pageTag: 'Label in het kader op /wedstrijd', pagePrize: 'Prijs in het kader op /wedstrijd', optionLabel: 'Naam van de keuzes', kind: 'Soort', videoFile: 'Video', audioFile: 'Geluidsfragment',
  cards: 'Kaarten aan of uit', actua: 'Actua', informative: 'Informatief', detection: 'Hoe detecteer je', saveTheDate: 'Save the date',
  contest: 'Wedstrijd', poll: 'Poll van de maand', firstAid: 'Eerste hulp bij', barometer: 'Desinformatie-barometer',
  principles: 'Principes', follow: 'Volg reeks', join: 'Meedoen',
  postal: 'Adres', lines: 'Regels', mail: 'E-mail', address: 'E-mailadres', organisation: 'Organisatie',
  hint: 'Tekst in het lege veld', email: 'E-mailadres', phone: 'Telefoonnummer', message: 'Bericht', consent: 'Akkoord met het privacybeleid',
  consentLink: 'Linktekst privacybeleid', submit: 'Knoptekst', success: 'Na het verzenden',
  banner: 'Banner onderaan', tipBox: 'Kader "Tip ons"', gameTeaser: 'Banner Mens vs machine', menuDisclaimer: 'Onderaan het menu',
  supportTitle: 'Kop boven het Vlaanderen-logo', summary: 'Korte uitleg', newsletter: 'Nieuwsbrief', columns: 'Kolommen', links: 'Links', socials: 'Sociale media', network: 'Netwerk',
  ids: 'Gekozen factchecks', categories: 'Categorieën', limit: 'Maximum aantal factchecks (1 tot 15)',
};

export function labelFor(key: string): string {
  if (LABELS[key]) return LABELS[key];
  const spaced = key.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/[-_]/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** `series[2].items[1].title` → ['series', 2, 'items', 1, 'title'], with the 1-based indexes of `validate`. */
export function pathParts(path: string): (string | number)[] {
  const parts: (string | number)[] = [];
  const pattern = /([^.[\]]+)|\[(\d+)\]/g;
  let match = pattern.exec(path);
  while (match) {
    parts.push(match[2] !== undefined ? Number(match[2]) : match[1]);
    match = pattern.exec(path);
  }
  return parts;
}

/** A validation path as an editor reads it: labels and item titles, e.g. "Reeksen › Gebundelde factchecks › Titel". */
export function describePath(path: string, value: unknown): string {
  const labels: string[] = [];
  let current = value;
  for (const part of pathParts(path)) {
    if (typeof part === 'number') {
      const item = Array.isArray(current) ? current[part - 1] : undefined;
      labels.push(itemSummary(item, part - 1));
      current = item;
    } else {
      labels.push(labelFor(part));
      current = isRecord(current) ? current[part] : undefined;
    }
  }
  return labels.join(' › ');
}

/** Whether an error is at `path` or inside it. */
export function isWithin(errorPath: string, path: string): boolean {
  if (!path) return true;
  return errorPath === path || errorPath.startsWith(`${path}.`) || errorPath.startsWith(`${path}[`);
}

const SUMMARY_KEYS = ['title', 'name', 'word', 'term', 'topic', 'theme', 'label', 'question', 'quote', 'text', 'lead'];

const shorten = (text: string): string => (text.length > 70 ? `${text.slice(0, 70)}…` : text);

/**
 * A round of a game (Mens of machine, Headline of Headlie) asks the same question every time, so without a title
 * of its own it is named after its first choice: the headline, the joke or the description of the photo.
 */
function roundSummary(item: Record<string, unknown>): string {
  const options = Array.isArray(item.options) ? item.options.filter(isRecord) : [];
  if (!options.some((option) => 'isReal' in option || 'isClickbait' in option)) return '';
  if (typeof item.title === 'string' && item.title.trim()) return shorten(item.title.trim());
  const [first] = options;
  const name = [first.text, first.alt].find((value): value is string => typeof value === 'string' && value.trim() !== '');
  // "Man roept voor brandende woontorens, versie 1": the version is the same for every round.
  return name ? shorten(name.trim().replace(/,?\s*versie \d+$/i, '')) : '';
}

function ownSummary(item: Record<string, unknown>): string {
  for (const key of SUMMARY_KEYS) {
    const candidate = item[key];
    if (typeof candidate === 'string' && candidate.trim()) return candidate.length > 70 ? `${candidate.slice(0, 70)}…` : candidate;
  }
  return '';
}

/**
 * Short name of a list item in the outline and its row: its own title, name, … or else that of a group
 * inside it (a factcheck of the week is named after its technique).
 */
export function itemSummary(item: unknown, index: number): string {
  if (isRecord(item)) {
    const round = roundSummary(item);
    if (round) return round;
    const own = ownSummary(item);
    if (own) return own;
    for (const value of Object.values(item)) {
      const nested = isRecord(value) ? ownSummary(value) : '';
      if (nested) return nested;
    }
  }
  if (typeof item === 'string' && item.trim()) return item.length > 70 ? `${item.slice(0, 70)}…` : item;
  return `Item ${index + 1}`;
}
